import { Injectable } from '@angular/core';
import { ChatHttpService } from 'app/http/services/chat/chat.service';
import { ChatSignalRService } from 'app/http/services/chat-signalr/chat-signalr.service';
import { ChatMessage, ChatSession } from 'app/layout/common/quick-chat/quick-chat.types';
import { BehaviorSubject, Observable, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class QuickChatService {
    private _selectedSession: BehaviorSubject<ChatSession | null> =
        new BehaviorSubject(null);
    private _messages: BehaviorSubject<ChatMessage[]> = new BehaviorSubject<
        ChatMessage[]
    >([]);
    /** Conversación que se pidió abrir desde una notificación (ver open-chat.guard.ts). */
    private _openRequest: BehaviorSubject<number | null> = new BehaviorSubject(null);

    constructor(
        private _chatHttp: ChatHttpService,
        private _chatSignalR: ChatSignalRService
    ) {}

    get selectedSession$(): Observable<ChatSession | null> {
        return this._selectedSession.asObservable();
    }

    get messages$(): Observable<ChatMessage[]> {
        return this._messages.asObservable();
    }

    get openRequest$(): Observable<number | null> {
        return this._openRequest.asObservable();
    }

    requestOpen(sessionId: number): void {
        this._openRequest.next(sessionId);
    }

    clearOpenRequest(): void {
        this._openRequest.next(null);
    }

    get sessions$(): Observable<ChatSession[]> {
        return this._chatSignalR.chatSessions$;
    }

    get newMessage$(): Observable<ChatMessage | null> {
        return this._chatSignalR.newMessage$;
    }

    get typingIndicator$(): Observable<any> {
        return this._chatSignalR.typingIndicator$;
    }

    loadSessions(): void {
        this._chatHttp.getActiveSessions().pipe(
            tap((res: any) => {
                if (res.succeeded) {
                    this._chatSignalR.updateSessions(res.data || []);
                }
            })
        ).subscribe();
    }

    selectSession(session: ChatSession): void {
        this._selectedSession.next(session);
        this._chatSignalR.joinSession(session.sessionIdentifier);
        this.loadMessages(session.id);
        this._chatHttp.markRead(session.id).subscribe();
    }

    clearSelection(): void {
        this._selectedSession.next(null);
        this._messages.next([]);
    }

    loadMessages(chatSessionId: number): void {
        this._chatHttp.getMessages(chatSessionId).pipe(
            tap((res: any) => {
                if (res.succeeded) {
                    this._messages.next(res.data || []);
                }
            })
        ).subscribe();
    }

    addMessage(message: ChatMessage): void {
        const current = this._messages.value;
        if (!current.some((m) => m.id === message.id)) {
            this._messages.next([...current, message]);
        }
    }

    sendMessage(
        chatSessionId: number,
        content: string,
        senderAdminUserId: string,
        senderName: string,
        sessionIdentifier: string
    ): Observable<any> {
        return this._chatHttp.sendMessage({
            chatSessionId,
            content,
            senderType: 'admin',
            senderAdminUserId,
            senderName,
            sessionIdentifier,
        });
    }

    assignAdmin(
        chatSessionId: number,
        adminUserId: string
    ): Observable<any> {
        return this._chatHttp.assignAdmin({ chatSessionId, adminUserId });
    }

    closeSession(chatSessionId: number): Observable<any> {
        return this._chatHttp.closeSession(chatSessionId);
    }

    sendTypingIndicator(
        sessionIdentifier: string,
        isTyping: boolean
    ): void {
        this._chatSignalR.sendTypingIndicator(sessionIdentifier, isTyping);
    }
}
